import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { storeTerminalsAPI } from '@/services/api';
import { useStoreTerminal } from '@/components/store/TerminalSelector';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Monitor, Plus, Edit, Trash2, CheckCircle } from 'lucide-react';

const TerminalsPage = () => {
  const { user } = useAuth();
  const storeId = user?.entity_id;
  const terminalContext = useStoreTerminal();

  const [terminals, setTerminals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTerminal, setEditingTerminal] = useState(null);
  const [newTerminalName, setNewTerminalName] = useState('');
  const [newTerminalType, setNewTerminalType] = useState('pos');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (storeId) {
      loadTerminals();
    }
  }, [storeId]);

  const loadTerminals = async () => {
    try {
      setLoading(true);
      const res = await storeTerminalsAPI.list(storeId);
      setTerminals(res.data.terminals || []);
    } catch (error) {
      console.error('Failed to load terminals:', error);
      toast.error('Failed to load terminals');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTerminal = async () => {
    if (!newTerminalName.trim()) {
      toast.error('Please enter terminal name');
      return;
    }

    try {
      setProcessing(true);
      await storeTerminalsAPI.create({
        store_id: storeId,
        terminal_name: newTerminalName,
        terminal_type: newTerminalType
      });

      toast.success('Terminal created successfully');
      setShowAddModal(false);
      setNewTerminalName('');
      setNewTerminalType('pos');
      loadTerminals();
      terminalContext?.refreshTerminals?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create terminal');
    } finally {
      setProcessing(false);
    }
  };

  const handleEditTerminal = async () => {
    if (!editingTerminal) return;

    try {
      setProcessing(true);
      await storeTerminalsAPI.update(editingTerminal.terminal_id, {
        terminal_name: newTerminalName,
        terminal_type: newTerminalType
      });

      toast.success('Terminal updated');
      setShowEditModal(false);
      setEditingTerminal(null);
      loadTerminals();
      terminalContext?.refreshTerminals?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update terminal');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteTerminal = async (terminalId) => {
    if (!window.confirm('Are you sure you want to deactivate this terminal?')) {
      return;
    }

    try {
      await storeTerminalsAPI.delete(terminalId);
      toast.success('Terminal deactivated');
      loadTerminals();
      terminalContext?.refreshTerminals?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete terminal');
    }
  };

  const openEditModal = (terminal) => {
    setEditingTerminal(terminal);
    setNewTerminalName(terminal.terminal_name);
    setNewTerminalType(terminal.terminal_type);
    setShowEditModal(true);
  };

  const handleSelectTerminal = (terminalId) => {
    terminalContext?.setSelectedTerminal?.(terminalId);
    toast.success(`Switched to ${terminalId}`);
  };

  return (
    <div className="p-6 space-y-6" data-testid="terminals-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Terminal Management</h1>
          <p className="text-gray-500">Configure and manage POS terminals for this store</p>
        </div>
        <Button
          onClick={() => {
            setNewTerminalName('');
            setNewTerminalType('pos');
            setShowAddModal(true);
          }}
          disabled={terminals.length >= 5}
          data-testid="add-terminal-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Terminal
        </Button>
      </div>

      {/* Current Terminal */}
      {terminalContext?.currentTerminal && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-blue-500 flex items-center justify-center">
                <Monitor className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-blue-600">Currently Selected</p>
                <p className="font-bold text-lg text-blue-900">
                  {terminalContext.currentTerminal.terminal_id} - {terminalContext.currentTerminal.terminal_name}
                </p>
              </div>
              <Badge className="ml-auto bg-blue-500">Active</Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Terminals Grid */}
      <Card>
        <CardHeader>
          <CardTitle>Terminals ({terminals.length}/5)</CardTitle>
          <CardDescription>
            Each terminal can have its own cash register and process sales independently
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading terminals...</div>
          ) : terminals.length === 0 ? (
            <div className="text-center py-12">
              <Monitor className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">No terminals configured</p>
              <p className="text-sm text-gray-400 mb-4">Create your first terminal to start billing</p>
              <Button onClick={() => setShowAddModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create First Terminal
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {terminals.map((terminal) => (
                <div
                  key={terminal.terminal_id}
                  className={`border rounded-lg p-4 ${
                    terminalContext?.selectedTerminal === terminal.terminal_id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        terminalContext?.selectedTerminal === terminal.terminal_id
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        <Monitor className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-bold">{terminal.terminal_id}</p>
                        <p className="text-sm text-gray-500">{terminal.terminal_name}</p>
                      </div>
                    </div>
                    {terminalContext?.selectedTerminal === terminal.terminal_id && (
                      <CheckCircle className="w-5 h-5 text-blue-500" />
                    )}
                  </div>

                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-gray-500 capitalize">
                      Type: {terminal.terminal_type}
                    </span>
                    {terminal.has_open_register && (
                      <Badge variant="secondary" className="bg-green-100 text-green-700">
                        Register Open
                      </Badge>
                    )}
                  </div>

                  <div className="flex gap-2">
                    {terminalContext?.selectedTerminal !== terminal.terminal_id && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => handleSelectTerminal(terminal.terminal_id)}
                      >
                        Select
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditModal(terminal)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-700"
                      onClick={() => handleDeleteTerminal(terminal.terminal_id)}
                      disabled={terminal.has_open_register}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Terminal Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Terminal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Terminal Name</Label>
              <Input
                value={newTerminalName}
                onChange={(e) => setNewTerminalName(e.target.value)}
                placeholder="e.g., Counter 1, Checkout A"
                data-testid="terminal-name-input"
              />
            </div>
            <div>
              <Label>Terminal Type</Label>
              <Select value={newTerminalType} onValueChange={setNewTerminalType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pos">POS Counter</SelectItem>
                  <SelectItem value="kiosk">Self-Service Kiosk</SelectItem>
                  <SelectItem value="mobile">Mobile Terminal</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddTerminal} disabled={processing}>
              {processing ? 'Creating...' : 'Create Terminal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Terminal Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Terminal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Terminal ID</Label>
              <Input value={editingTerminal?.terminal_id || ''} disabled />
            </div>
            <div>
              <Label>Terminal Name</Label>
              <Input
                value={newTerminalName}
                onChange={(e) => setNewTerminalName(e.target.value)}
              />
            </div>
            <div>
              <Label>Terminal Type</Label>
              <Select value={newTerminalType} onValueChange={setNewTerminalType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pos">POS Counter</SelectItem>
                  <SelectItem value="kiosk">Self-Service Kiosk</SelectItem>
                  <SelectItem value="mobile">Mobile Terminal</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditTerminal} disabled={processing}>
              {processing ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TerminalsPage;
